import { OkrMethodologyFacade } from "./../components/facades/okr-methodology.facade";
import { IAngularEvent, IPromise, IQService, noop } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalScope } from "angular-ui-bootstrap";
import { AnalyticsService, Flavor, recommendation } from "@gtmhub/core/analytics";
import { IStateInit } from "@gtmhub/core/routing";
import { ITeam, ITeamsStoreState, TeamsActions, TeamService } from "@gtmhub/teams";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { CfMap } from "@gtmhub/customFields";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import {
  ISessionsStoreState,
  PlanningSessionService,
  IPlanningSession,
  PlanningSessionsActions,
  filterOpenedActiveAndFutureSessionsAndOneLevelChildren,
  filterOpenedActiveAndFutureSessions,
} from "@gtmhub/sessions";
import { storage } from "@gtmhub/core/storage";
import { IRestLayerRequest, extendRestLayerFilterWithAnd } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { getCurrentUserId, PermissionsService } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGtmhubRootScopeService, ICollection } from "@gtmhub/models";
import { IPolicy } from "@gtmhub/policies/models/models";
import { PolicyService } from "@gtmhub/policies/services/policyService";
import { GoalService } from "../goal.service";
import { GoalFormEventType, IGoal, IGoalParent } from "../models";
import { MISSING_SESSION_ID_STATE_PARAM } from "./all";
import { GoalsChangedEventArgs, goalSelected, metricSelected, OkrEvents } from "@gtmhub/okrs";
import { IParentOkrSelectorEntry } from "@gtmhub/shared/components/parent-okr-selector/models";
import { IGoalMethodologySettingsUI } from "../components/facades/okr-methodology.facade";
import { GoalParentType } from "@webapp/okrs/goals/models/goal.models";

interface IEditGoalFormRedux {
  assigneeIdMap: IdMap<IAssignee>;
  sessionIdMap: Record<string, IPlanningSession>;
  sessions: IPlanningSession[];
  teams: ITeam[];
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState): IEditGoalFormRedux => {
  const { map: assigneeIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const { map: sessionIdMap, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { items: teams, isFetched: teamsFetched, error: teamsError } = state.teams;
  return {
    assigneeIdMap,
    sessionIdMap: sessionIdMap,
    sessions: state.sessions.items,
    teams,
    reduxItemsFetched: assigneesFetched && sessionsFetched && teamsFetched,
    reduxItemsError: assigneesError || sessionsError || teamsError,
  };
};

export interface IGoalStateParams {
  externalSessionId: string;
  teamId: string;
  id: string;
  parentId: string;
  parentType: GoalParentType;
  planningSessionId: string;
  returnState: string;
}

export interface IEditGoalCtrlScope extends IModalScope, IExtendedRootScopeService, IEditGoalFormRedux {
  hasPlanningSessionId: boolean;
  defaultOwner: {
    name: string;
    goalsCount: number;
  };
  screenAllowed: boolean;
  goalsLimitReach: boolean;
  parentContainer: {
    parentGoal: IGoal;
    parentMetric: IMetric;
    planningSession: IPlanningSession;
  };
  goal: IGoal;
  parentOkr: Partial<IParentOkrSelectorEntry>;
  goalNamePolicy: IPolicy;
  indicators: {
    loading: boolean;
    saving: boolean;
    loadingPolicy: boolean;
  };
  rawGoal: IGoal;
  originalSessionId: string;
  sessionSearch: string;
  isAlignable: boolean;
  teamAssigneeForbiddenMessage: boolean;
  invalidAssigneeErrorMessage: boolean;
  isPrivateGoalAllowed: boolean;
  requiredCfNotEmpty: boolean;
  isCurrentSessionVisible: boolean;
  isSearchInputVisible: boolean;
  stateParams: IGoalStateParams;
  selectedCurrentSession: IPlanningSession;
  hostSession: IPlanningSession;
  currentFormEnv: FormEnvironment;
  methodologySettings: IGoalMethodologySettingsUI;

  save(): void;
  selectAssignee(assignee: IAssignee): void;
  closeGoalsLimitWarning(): void;
  checkForGoalPrivacyAndAssigneeType(): void;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectMultipleOwners(ids: string[]): void;
  showCurrentSession(): void;
  hideCurrentSession(): void;
  showSessionSelector(sessionSearch: string): void;
  selectParentSession(session: string): void;
  selectParentOkr(parent: IGoalParent): void;
  shouldDisableActionButton(): boolean;
  getAvailableSessions(options: { parentSessionId?: string }, sessions: IPlanningSession[]): IPlanningSession[];
  filterActiveSessionsWithChildren(): IPlanningSession[];
  openAlignmentTree(): void;
  resetInvalidAssigneesError(): void;
}
interface IFormCacheValues {
  goal: IGoal;
  parentGoal: IGoal;
  parentMetric: IMetric;
}

export enum FormEnvironment {
  CREATE_GOAL_FORM = "createGoalForm",
  CREATE_CHILD_GOAL_FORM = "createChildGoalForm",
}

type FormCache = Record<FormEnvironment, IFormCacheValues>;

const formCache: FormCache = {
  createGoalForm: {
    goal: null,
    parentGoal: null,
    parentMetric: null,
  },
  createChildGoalForm: {
    goal: null,
    parentGoal: null,
    parentMetric: null,
  },
};

export const exposeCache = () => angular.copy(formCache); // only for testing purposes not to export the cache object
export const updateCache = (currentFormEnv: FormEnvironment, scope?: IEditGoalCtrlScope): void => {
  formCache[currentFormEnv].goal = scope ? scope.goal : null;
  formCache[currentFormEnv].parentGoal = scope ? scope.parentContainer.parentGoal : null;
  formCache[currentFormEnv].parentMetric = scope ? scope.parentContainer.parentMetric : null;
};
let initialLoad = true;
let cacheTimestamp = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minuts in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};

export class EditGoalCtrl implements IStateInit {
  private currentUserId: string;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$stateParams",
    "$state",
    "GoalService",
    "PlanningSessionService",
    "PermissionsService",
    "UIErrorHandlingService",
    "$q",
    "PolicyService",
    "TeamService",
    "AnalyticsService",
    "MetricService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "TeamsActions",
    "$ngRedux",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IEditGoalCtrlScope,
    private $stateParams: IGoalStateParams,
    private $state: StateService,
    private goalService: GoalService,
    private planningSessionService: PlanningSessionService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $q: IQService,
    private policyService: PolicyService,
    private teamService: TeamService,
    private analyticsService: AnalyticsService,
    private metricService: MetricService,
    private assigneeActions: AssigneeActions,
    private sessionActions: PlanningSessionsActions,
    private teamsActions: TeamsActions,
    private $ngRedux: INgRedux,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    // creating the goal object so sessionId can be added during permissions or replaced with existing goal
    this.$scope.goal = {
      ownerIds: [],
      description: "",
      name: "",
      attainmentTypeString: "average_kr",
      dateFrom: "",
      dateTo: "",
      customFields: {},
    };

    this.$scope.parentContainer = {
      parentGoal: null,
      parentMetric: null,
      planningSession: null,
    };

    this.$scope.hasPlanningSessionId = !!this.$stateParams.planningSessionId;
    this.$scope.currentFormEnv = this.$stateParams.parentId ? FormEnvironment.CREATE_CHILD_GOAL_FORM : FormEnvironment.CREATE_GOAL_FORM;
    const metaGoalFormOpened = { type: "create" };

    this.analyticsService.track(GoalFormEventType.GOAL_FORM_OPENED, metaGoalFormOpened);

    this.$scope.checkForGoalPrivacyAndAssigneeType = this.checkForGoalPrivacyAndAssigneeType;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.shouldDisableActionButton = this.shouldDisableActionButton;
    this.$scope.showCurrentSession = this.showCurrentSession;
    this.$scope.hideCurrentSession = this.hideCurrentSession;
    this.$scope.showSessionSelector = this.showSessionSelector;
    this.$scope.selectParentSession = this.selectParentSession;
    this.$scope.selectParentOkr = this.selectParentOkr;
    this.$scope.openAlignmentTree = this.openAlignmentTree;
    this.$scope.getAvailableSessions = getAvailableSessions;
    this.$scope.resetInvalidAssigneesError = this.resetInvalidAssigneesError;
    this.$scope.filterActiveSessionsWithChildren = () =>
      getAvailableSessions({ parentSessionId: this.$scope.hostSession ? this.$scope.hostSession.id : null }, this.$scope.sessions);

    this.$rootScope.ui = {
      showGoalTitle: true,
      maskGrid: true,
    };

    this.$scope.save = this.save;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;

    this.$scope.closeGoalsLimitWarning = this.closeGoalsLimitWarning;

    this.$scope.rawGoal = {};
  }

  private onExit(event: IAngularEvent): void {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    const scope = event.currentScope as IEditGoalCtrlScope;
    const currentFormEnv = scope.currentFormEnv;
    initialLoad = false;

    if (modalCloseReason && modalCloseReason.includes("backdrop")) {
      cacheTimestamp = Date.now();
      updateCache(currentFormEnv, scope);
    } else {
      updateCache(currentFormEnv);
    }
  }

  public stateInit(): IPromise<unknown> {
    this.$scope.indicators = {
      loading: true,
      saving: false,
      loadingPolicy: false,
    };

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.$on("$destroy", this.onExit);

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.sessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          this.getGoalPolicy();
          return this.init();
        },
        (error) => this.handleIndicatorError(error, "loading")
      );
  }

  private checkForGoalPrivacyAndAssigneeType = (): void => {
    const { goal } = this.$scope;
    const hasTeamAssignee = goal.ownerIds.some((id) => this.$scope.assigneeIdMap[id] && this.$scope.assigneeIdMap[id].type === "team");
    if (goal.private && hasTeamAssignee) {
      this.assignOwner(this.currentUserId);
      this.$scope.teamAssigneeForbiddenMessage = true;
    } else {
      this.$scope.teamAssigneeForbiddenMessage = false;
    }
  };

  private getGoalPolicy = (): IPromise<IPolicy | void> => {
    this.$scope.indicators.loadingPolicy = true;

    return this.policyService.getPolicies().then(
      (allPolicies) => {
        this.$scope.indicators.loadingPolicy = false;
        this.$scope.goalNamePolicy = allPolicies.filter((policy: IPolicy) => policy.fieldName === "Name")[0];
      },
      (error) => this.handleIndicatorError(error, "loadingPolicy", true)
    );
  };

  private init = (): void => {
    this.currentUserId = getCurrentUserId();
    this.$scope.hostSession = this.getCurrentSession();
    this.$scope.selectedCurrentSession = this.$scope.sessionIdMap && this.$scope.hostSession ? this.$scope.sessionIdMap[this.$scope.hostSession.id] : null;

    this.$scope.sessions = this.$scope.getAvailableSessions({ parentSessionId: this.$scope.hostSession ? this.$scope.hostSession.id : null }, this.$scope.sessions);

    this.$scope.stateParams = this.$stateParams;
    this.$scope.isCurrentSessionVisible = this.$scope.hasPlanningSessionId || !!this.$scope.selectedCurrentSession;
    this.$scope.isSearchInputVisible = false;
    this.$scope.sessionSearch = "";

    this.checkAndSetCachedGoal();

    if (this.$stateParams.parentId && !(this.$scope.parentContainer.parentGoal || this.$scope.parentContainer.parentMetric)) {
      this.generateGoalParentPromise(this.$stateParams.parentId, this.$stateParams.parentType).then((parent) => {
        this.setParentAndSessionId(parent);
        this.setInitialData();
      });
    } else {
      this.$scope.goal.sessionId = this.$stateParams.planningSessionId;
      this.setInitialData();
    }
  };

  private setPrivateGoalAllowed = (): void => {
    this.$scope.isPrivateGoalAllowed = this.$scope.methodologySettings.assignGoalPermissions
      ? this.$scope.methodologySettings.assignGoalPermissions.includes("user")
      : true;
  };

  private checkAndSetCachedGoal = () => {
    if (!isCacheTimedout() && !initialLoad) {
      if (formCache[this.$scope.currentFormEnv].goal) {
        this.$scope.goal = formCache[this.$scope.currentFormEnv].goal;
        this.$scope.goal.sessionId = this.$stateParams.planningSessionId || null;
      }
      if (!this.$stateParams.parentId) {
        if (formCache[this.$scope.currentFormEnv].parentGoal) this.$scope.parentContainer.parentGoal = formCache[this.$scope.currentFormEnv].parentGoal;
        if (formCache[this.$scope.currentFormEnv].parentMetric) this.$scope.parentContainer.parentMetric = formCache[this.$scope.currentFormEnv].parentMetric;
      }
    }
  };

  private setParentAndSessionId = (parent?: IGoal | IMetric) => {
    this.setParentGoalInParentContainer(parent, this.$stateParams.parentType);
    this.$scope.goal.parentId = parent.id;
    this.$scope.goal.parentType = this.$stateParams.parentType;
    this.$scope.goal.sessionId = parent.sessionId;
    this.$scope.parentOkr = {
      id: parent.id,
      type: this.$stateParams.parentType,
    };
  };

  private getCurrentSession = (): IPlanningSession => {
    let currentSession: IPlanningSession;
    // planningSession could be passed as a string "none" in the address bar that's why it is added in the condition
    if (this.$stateParams.planningSessionId && this.$stateParams.planningSessionId !== MISSING_SESSION_ID_STATE_PARAM && !this.$stateParams.externalSessionId) {
      currentSession = this.$scope.sessionIdMap[this.$stateParams.planningSessionId];
    } else if (this.$stateParams.externalSessionId) {
      this.$scope.goal.sessionId = this.$stateParams.externalSessionId;
      currentSession = this.$scope.sessionIdMap[this.$stateParams.externalSessionId];

      if (this.$stateParams.teamId) {
        this.assignOwner(this.$stateParams.teamId);
      }
    }

    // check if there is a planningSession, this is needed to get the allowedActions
    // if there is no planningSession we could get to the allowedActions though the parentGoal if there is one
    // if we are creating a sub-objective
    // check if goal has session in order to make a sub-objective
    if (!currentSession && this.$scope.parentContainer.parentGoal && this.$scope.parentContainer.parentGoal.sessionId) {
      this.$scope.goal.sessionId = this.$scope.parentContainer.parentGoal.sessionId;
      currentSession = this.$scope.sessionIdMap[this.$scope.parentContainer.parentGoal.sessionId];
    }

    return currentSession;
  };

  private setInitialData = () => {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          const currentSession = this.$scope.hostSession;
          this.$scope.isAlignable = false;

          if (currentSession) {
            this.setPlanningSession(currentSession);
            const targetId = this.$scope.parentContainer.planningSession.id;
            const targetType = "session";

            this.$q
              .all({
                alignableGoals: this.$scope.goal.sessionId ? this.getAlignableGoal() : null,
                currentUserAllowedActions: this.permissionsService.getCurrentUserAllowedActionsPerItem(targetId, targetType),
              })
              .then(
                ({ alignableGoals, currentUserAllowedActions }) => {
                  this.$scope.screenAllowed = currentUserAllowedActions.currentUserActions.includes("create");

                  if (alignableGoals) {
                    this.$scope.isAlignable = alignableGoals.totalCount > 0;
                  }

                  this.$scope.indicators.loading = false;
                },
                (err) => this.handleIndicatorError(err, "loading", true)
              );
          } else {
            this.$scope.hasPlanningSessionId = false;

            if (this.$scope.goal.sessionId) {
              this.getAlignableGoal().then(
                (alignableGoals) => {
                  this.$scope.screenAllowed = true;

                  if (alignableGoals) {
                    this.$scope.isAlignable = alignableGoals.totalCount > 0;
                  }

                  this.$scope.indicators.loading = false;
                },
                (err) => this.handleIndicatorError(err, "loading", true)
              );
            } else {
              this.$scope.screenAllowed = true;
              this.$scope.indicators.loading = false;
            }
          }

          this.setMethodologySettings();
          this.setPrivateGoalAllowed();
          this.checkOwnerPermissionAndAssignOwner();
        },
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  };

  private setMethodologySettings = (): void => {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForGoalView(this.$scope.goal, this.$scope.parentContainer.planningSession);
  };

  private getCurrentUserTeam = (): IPromise<string | void> => {
    const queryGettingCurrentUserTeam = {
      filter: { $or: [{ members: { $in: [this.currentUserId] } }, { manager: this.currentUserId }] },
      sort: "name",
      take: 1,
      fields: "id",
    };

    // The goal should be assigned to a team which the current user is a part or is manager of or if no such exists, just a team
    return this.teamService.getTeams(queryGettingCurrentUserTeam).then(
      (teams: ICollection<ITeam>) => {
        return teams.items.length ? teams.items[0].id : this.$scope.teams[0].id;
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onParentSelected(args.detail.goal);
      return;
    }
    if (args.reason === metricSelected) {
      this.onParentSelected(args.detail.parent);
    }
  };

  private onParentSelected = (parent: IGoal | IMetric): void => {
    const isMetric = "goalName" in parent;

    if (isMetric) {
      this.$scope.parentContainer.parentMetric = parent as IMetric;
    } else {
      this.$scope.parentContainer.parentGoal = parent as IGoal;
    }

    this.$scope.goal.parentId = parent.id;
  };

  private openAlignmentTree = (): void => {
    this.$state.go("gtmhub.goals.alignmentSidebar.tree", { planningSessionId: this.$scope.goal.sessionId });
  };

  private selectParentOkr = (parent: IGoalParent): void => {
    if (parent) {
      parent.type === "goal"
        ? this.goalService.getGoal(parent.id).then((parentGoal) => {
            this.$scope.parentContainer.parentGoal = parentGoal;
            this.$scope.goal.parentId = parent.id;
          })
        : this.metricService.getMetric(parent.id).then((parentMetric) => {
            this.$scope.parentContainer.parentMetric = parentMetric;
            this.$scope.goal.parentId = parent.id;
          });
    } else {
      this.$scope.goal.parentId = "";
      this.$scope.parentContainer.parentGoal = null;
      this.$scope.parentContainer.parentMetric = null;
    }
  };

  private generateGoalParentPromise = (parentId: string, parentType: GoalParentType): IPromise<IGoal | IMetric> => {
    return parentType === "metric" ? this.metricService.getMetric(parentId) : this.goalService.getGoal(parentId);
  };

  private setParentGoalInParentContainer = (parent: IGoal | IMetric, parentType: GoalParentType) => {
    if (parentType === "metric") {
      this.$scope.parentContainer.parentMetric = parent as IMetric;
    } else {
      this.$scope.parentContainer.parentGoal = parent as IGoal;
    }
  };

  private save = (): void => {
    this.$rootScope.traceAction("create_OKR", () => {
      this.$scope.indicators.saving = true;

      if (this.$scope.parentContainer.planningSession) {
        this.$scope.goal.dateFrom = this.$scope.parentContainer.planningSession.start;
        this.$scope.goal.dateTo = this.$scope.parentContainer.planningSession.end;
      }

      this.goalService.createGoal(this.$scope.goal, { skipNotification: this.$scope.parentContainer.planningSession.demo }).then(
        (goal) => {
          this.$rootScope.$broadcast("goalsChanged");
          this.trackRecommendationHelpfulness(goal);

          this.$scope.indicators.saving = false;
          this.$rootScope.ui.maskGrid = false;

          this.$state.go("^.^").then(() => {
            if (!this.$state.current.name.includes("_goalSidebar")) {
              this.$state.go("._goalSidebar.goal", { id: goal.id });
            } else {
              this.$state.go(".", { id: goal.id });
            }
          });
        },
        (err) => this.handleIndicatorError(err, "saving", true)
      );
    });
  };

  private trackRecommendationHelpfulness(gol: IGoal): void {
    //Tracking goal creation recommendations
    if (!this.$stateParams.parentId) {
      //recommendation is helpful if user doesn’t remove themselves as owner
      this.analyticsService.track(recommendation, {
        is_useful: gol.ownerIds.includes(getCurrentUserId()),
        flavor: Flavor.SMART_DEFAULT,
        action: "creates_goal;owner",
      });
      if (this.$scope.hostSession) {
        //recommendation is helpful if user doesn’t change current session
        this.analyticsService.track(recommendation, {
          is_useful: gol.sessionId == this.$scope.hostSession.id,
          flavor: Flavor.SMART_DEFAULT,
          action: "creates_goal;session",
        });
      }
    }
    //Tracking child goal creation recommendations
    else {
      //recommendation is helpful if user doesn’t remove themselves as owner
      this.analyticsService.track(recommendation, {
        is_useful: gol.ownerIds.includes(getCurrentUserId()),
        flavor: Flavor.SMART_DEFAULT,
        action: "creates_child_goal;owner",
      });
      if (this.$scope.hostSession) {
        //recommendation is helpful if user doesn’t change parent session
        this.analyticsService.track(recommendation, {
          is_useful: gol.sessionId == this.$scope.hostSession.id,
          flavor: Flavor.SMART_DEFAULT,
          action: "creates_child_goal;parent_goal",
        });
      }
    }
  }

  private assignOwner = (id: string): void => {
    this.checkGoalLimit(id)
      .then((limitReached) => {
        if (!limitReached) {
          this.selectMultipleOwners([id]);
        }
      })
      .catch(noop);
  };

  private closeGoalsLimitWarning = () => {
    this.$scope.goalsLimitReach = false;
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) return;

    this.$scope.goal.ownerIds = ids;
  };

  private setPlanningSession = (session: IPlanningSession) => {
    this.$scope.hasPlanningSessionId = true;
    this.$scope.parentContainer.planningSession = session;
  };

  private checkGoalLimit = (id: string): IPromise<boolean> => {
    if (!this.$scope.parentContainer.planningSession || !id) {
      return this.$q.reject("missing planning session and/or selected owner");
    }

    return this.planningSessionService.stats(this.$scope.parentContainer.planningSession.id, { assigneeId: id }).then(
      (stats) => {
        this.$scope.defaultOwner = {
          name: this.$scope.assigneeIdMap[id]?.name,
          goalsCount: stats.goalCount,
        };

        this.$scope.goalsLimitReach = this.$scope.defaultOwner.goalsCount >= this.$scope.methodologySettings.goalsPerOwner.limit;

        if (this.$scope.goalsLimitReach) {
          this.$scope.indicators.saving = false;
        }

        return this.$scope.goalsLimitReach;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.reject(error);
      }
    );
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private getAlignableGoal = (): IPromise<ICollection<IGoal>> => {
    const sessionIds = [this.$scope.goal.sessionId];
    if (
      this.$scope.parentContainer.planningSession &&
      this.$scope.parentContainer.planningSession.parentId &&
      !sessionIds.includes(this.$scope.parentContainer.planningSession.parentId)
    ) {
      sessionIds.push(this.$scope.parentContainer.planningSession.parentId);
    }

    const request: IRestLayerRequest = { filter: { sessionId: { $in: sessionIds } }, flatten: "cf", fields: "ownerIds", limit: 1 };
    if (this.$scope.goal.id) {
      extendRestLayerFilterWithAnd(request, { _id: { $nin: [this.$scope.goal.id] } });
    }
    return this.goalService.getGoalsV2(request);
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.goal.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private shouldDisableActionButton = (): boolean => {
    const isOwnerInvalid = !this.$scope.goal.ownerIds.length;

    return !this.$scope.goal.sessionId || isOwnerInvalid || this.$scope.goal.locked || !this.$scope.requiredCfNotEmpty || this.$scope.indicators.saving;
  };

  private showCurrentSession = (): void => {
    this.$scope.sessionSearch = "";
    this.$scope.isCurrentSessionVisible = true;
  };

  private hideCurrentSession = (): void => {
    setTimeout(() => document.getElementById("sessionSearch").focus());
    this.$scope.isCurrentSessionVisible = false;
    this.$scope.isSearchInputVisible = false;
    this.$scope.sessionSearch = "";
  };

  private showSessionSelector = (sessionSearch: string): void => {
    this.$scope.sessionSearch = sessionSearch;
    if (this.$scope.sessionSearch && this.$scope.sessionSearch.length > 0) {
      this.$scope.isSearchInputVisible = true;
    } else {
      this.$scope.isSearchInputVisible = false;
    }
  };

  private checkOwnerPermissionAndAssignOwner = (): void => {
    if (this.$scope.methodologySettings.assignGoalPermissions === "team") {
      this.getCurrentUserTeam().then((id) => {
        this.assignOwner(id as string);
      });
    } else {
      this.assignOwner(this.currentUserId);
    }
  };

  private selectParentSession = (sessionId: string): void => {
    this.$scope.selectedCurrentSession = this.$scope.sessionIdMap[sessionId];
    this.$scope.goal.sessionId = sessionId;
    this.$scope.isCurrentSessionVisible = true;
    this.$scope.isSearchInputVisible = false;

    const isPlanningSessionForChange = !this.$scope.parentContainer.planningSession || this.$scope.parentContainer.planningSession.id !== sessionId;
    if (isPlanningSessionForChange) {
      this.setPlanningSession(this.$scope.sessionIdMap[sessionId]);
      this.setMethodologySettings();
    }

    if (this.$scope.goal.ownerIds.length) {
      this.checkForInvalidAssignees();
    } else {
      this.resetInvalidAssigneesError();
      this.checkOwnerPermissionAndAssignOwner();
    }

    if (this.$scope.goal.sessionId) {
      this.getAlignableGoal().then((alignableGoals) => {
        this.$scope.isAlignable = alignableGoals.totalCount > 0;
        this.$scope.indicators.loading = false;
      });
    }
  };

  private checkForInvalidAssignees = (): void => {
    if (this.$scope.methodologySettings.assignGoalPermissions === "both") {
      return;
    }

    const invalidAssignee = this.$scope.goal.ownerIds.some(
      (ownerId) => this.$scope.assigneeIdMap[ownerId].type !== this.$scope.methodologySettings.assignGoalPermissions
    );

    if (invalidAssignee) {
      this.$scope.invalidAssigneeErrorMessage = true;
      this.$scope.goal.ownerIds = [];
    }
  };

  private resetInvalidAssigneesError = (): void => {
    if (this.$scope.invalidAssigneeErrorMessage) {
      this.$scope.invalidAssigneeErrorMessage = false;
    }
  };
}

export const getAvailableSessions = (options: { parentSessionId?: string } = {}, sessions: IPlanningSession[]): IPlanningSession[] => {
  let activeSessions: IPlanningSession[];

  if (options.parentSessionId) {
    activeSessions = filterOpenedActiveAndFutureSessionsAndOneLevelChildren(sessions, options.parentSessionId);
  } else {
    activeSessions = filterOpenedActiveAndFutureSessions(sessions);
  }
  return activeSessions;
};
